import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Building2, Phone, Calendar, FileText, Shield, Clock,
    ChevronDown, ChevronRight, Search, AlertTriangle,
    CheckCircle, Users, BookOpen, X
} from 'lucide-react';
import { campusIntelligence, officialDocuments } from '../services/api';

/**
 * CampusIntelligenceHub — Shared campus context surface.
 *
 * Renders inline in the dashboard for all roles.
 * Prominence is controlled by the parent (primary vs. secondary placement).
 *
 * Data sources:
 *   - Campus Intelligence API (contacts, events, policies, schedules)
 *   - Official Documents API (active docs — bell schedule, calendar, etc.)
 *
 * Always fails gracefully — empty state is warm, not broken.
 * Designed to be reusable as a flyout panel later if needed.
 */

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CI_ENABLED = (() => {
    try {
        const flags = JSON.parse(localStorage.getItem('saige_feature_flags') || '{}');
        return !!(flags.campus_intelligence_admin_ui || flags.campus_intelligence);
    } catch { return false; }
})();

const MotionDiv = motion.div;

const scopeBadge = (scope) => {
    const styles = {
        district: { bg: 'rgba(37, 99, 235, 0.14)', text: '#60A5FA', label: 'District' },
        campus:   { bg: 'rgba(22, 163, 74, 0.14)', text: '#22C55E', label: 'Campus' },
        personal: { bg: 'rgba(194, 65, 12, 0.14)', text: '#FB923C', label: 'Personal' },
    };
    const s = styles[scope] || styles.campus;
    return (
        <span style={{
            background: s.bg, color: s.text,
            fontSize: 10, fontWeight: 600,
            padding: '2px 6px', borderRadius: 4,
            letterSpacing: '0.02em', flexShrink: 0,
        }}>
            {s.label}
        </span>
    );
};


const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    try {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const target = new Date(dateStr + 'T00:00:00');
        const diff = Math.round((target - today) / 86400000);
        if (diff < 0) return null;
        if (diff === 0) return 'Today';
        if (diff === 1) return 'Tomorrow';
        if (diff <= 7) return `In ${diff} days`;
        return null;
    } catch { return null; }
};


// ─── Sub-sections ─────────────────────────────────────────────────────────────

const ContactRow = ({ contact }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 0', borderBottom: '1px solid var(--border-default)',
    }}>
        <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'linear-gradient(135deg, #FFF3CD, #FFE082)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
        }}>
            <Users size={14} color="#B45309" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                {contact.name}
            </div>
            {contact.title && (
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{contact.title}</div>
            )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
            {contact.phone && (
                <a
                    href={`tel:${contact.phone}`}
                    style={{ fontSize: 11, color: '#2563EB', display: 'flex', alignItems: 'center', gap: 3, textDecoration: 'none' }}
                >
                    <Phone size={10} /> {contact.phone}
                </a>
            )}
            {contact.extension && (
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>ext. {contact.extension}</span>
            )}
        </div>
    </div>
);

const EventRow = ({ event }) => {
    const urgency = daysUntil(event.date);
    const isUrgent = urgency === 'Today' || urgency === 'Tomorrow';
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 0', borderBottom: '1px solid var(--border-default)',
        }}>
            <div style={{
                width: 36, flexShrink: 0, textAlign: 'center',
            }}>
                <div style={{
                    fontSize: 12, fontWeight: 700,
                    color: isUrgent ? '#DC2626' : 'var(--text-secondary)',
                    lineHeight: 1.1,
                }}>
                    {event.date ? new Date(event.date + 'T00:00:00').getDate() : '—'}
                </div>
                <div style={{
                    fontSize: 9, fontWeight: 500, textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: isUrgent ? '#DC2626' : 'var(--text-muted)',
                }}>
                    {event.date ? new Date(event.date + 'T00:00:00').toLocaleString('en-US', { month: 'short' }) : ''}
                </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                    {event.title || event.name}
                </div>
                {event.description && (
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                        {event.description.length > 80 ? event.description.slice(0, 80) + '…' : event.description}
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                {urgency && (
                    <span style={{
                        fontSize: 10, fontWeight: 600,
                        background: isUrgent ? 'rgba(220, 38, 38, 0.16)' : 'var(--surface-raised)',
                        color: isUrgent ? '#DC2626' : 'var(--text-secondary)',
                        padding: '2px 6px', borderRadius: 4,
                    }}>
                        {urgency}
                    </span>
                )}
                {event.scope && scopeBadge(event.scope)}
            </div>
        </div>
    );
};

const PolicyRow = ({ policy }) => (
    <div style={{
        padding: '8px 12px', borderRadius: 8,
        background: 'var(--surface-raised)', border: '1px solid var(--border-default)',
        marginBottom: 6,
    }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={13} color="#64748b" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {policy.title || policy.policy_type}
                </span>
            </div>
            {policy.scope && scopeBadge(policy.scope)}
        </div>
        {policy.summary && (
            <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
                {policy.summary.length > 120 ? policy.summary.slice(0, 120) + '…' : policy.summary}
            </p>
        )}
    </div>
);

// ─── Doc Quick-Link ────────────────────────────────────────────────────────────

const DocTypeLabels = {
    bell_schedule:     { label: "Bell Schedule",       IconComp: Clock },
    academic_calendar: { label: "District Calendar",   IconComp: Calendar },
    testing_dates:     { label: "Testing Dates",       IconComp: AlertTriangle },
    student_handbook:  { label: "Student Handbook",    IconComp: BookOpen },
    campus_procedures: { label: "Campus Procedures",   IconComp: Building2 },
    lesson_plan_template: { label: "Lesson Template",  IconComp: FileText },
};

const DocQuickLink = ({ doc }) => {
    const meta = DocTypeLabels[doc.doc_type] || { label: doc.title, IconComp: FileText };
    const Icon = meta.IconComp;
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 10px', borderRadius: 8,
            background: 'var(--surface-card)', border: '1px solid var(--border-default)',
            cursor: 'default',
        }}>
            <div style={{
                width: 28, height: 28, borderRadius: 6,
                background: 'linear-gradient(135deg, #FFFBEB, #FEF3C7)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                <Icon size={13} color="#D97706" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{meta.label}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{doc.scope || 'campus'}</div>
            </div>
            <CheckCircle size={12} color="#22C55E" style={{ flexShrink: 0 }} />
        </div>
    );
};

// ─── Section Wrapper ──────────────────────────────────────────────────────────

const CollapsibleSection = ({ title, icon, children, defaultOpen = true, count }) => {
    const [open, setOpen] = useState(defaultOpen);
    const SectionIcon = icon;
    return (
        <div style={{ marginBottom: 16 }}>
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 8px',
                    borderBottom: open ? '1px solid var(--border-default)' : 'none',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <SectionIcon size={15} color="var(--text-secondary)" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
                        {title}
                    </span>
                    {count != null && (
                        <span style={{
                            fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)',
                            background: 'var(--surface-raised)', padding: '1px 6px', borderRadius: 10,
                        }}>
                            {count}
                        </span>
                    )}
                </div>
                {open
                    ? <ChevronDown size={14} color="var(--text-muted)" />
                    : <ChevronRight size={14} color="var(--text-muted)" />
                }
            </button>
            <AnimatePresence>
                {open && (
                    <MotionDiv
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div style={{ paddingTop: 10 }}>
                            {children}
                        </div>
                    </MotionDiv>
                )}
            </AnimatePresence>
        </div>
    );
};


// ─── Empty State ──────────────────────────────────────────────────────────────

const EmptyState = ({ userRole }) => {
    const messages = {
        admin: {
            title: 'Campus Intelligence is ready',
            body: 'Upload official documents in the Admin Workspace to populate contacts, events, schedules, and policies here.',
        },
        counselor: {
            title: 'Campus context will appear here',
            body: 'When your admin loads campus information, you\'ll see contacts, events, and procedures in this panel.',
        },
        support_staff: {
            title: 'Campus info coming soon',
            body: 'Campus procedures, contacts, and calendar will appear here once your admin loads campus information.',
        },
        teacher: {
            title: 'Campus context will appear here',
            body: 'Your admin can load campus calendars, bell schedules, and procedures that will appear here.',
        },
    };
    const msg = messages[userRole] || messages.teacher;

    return (
        <div style={{
            padding: '24px 20px', textAlign: 'center',
            background: 'var(--surface-raised)',
            borderRadius: 10, border: '1px dashed var(--border-strong)',
        }}>
            <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: 'linear-gradient(135deg, #FFF3CD, #FFE082)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 12px',
            }}>
                <Building2 size={20} color="#B45309" />
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>
                {msg.title}
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: 280, margin: '0 auto' }}>
                {msg.body}
            </p>
        </div>
    );
};


// ─── Main Component ───────────────────────────────────────────────────────────

const CampusIntelligenceHub = ({ userRole = 'teacher', prominence = 'secondary' }) => {
    const [contacts, setContacts] = useState([]);
    const [events, setEvents] = useState([]);
    const [policies, setPolicies] = useState([]);
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [collapsed, setCollapsed] = useState(prominence === 'secondary');
    const [hasData, setHasData] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                if (CI_ENABLED) {
                    const [contactsRes, eventsRes, policiesRes] = await Promise.allSettled([
                        campusIntelligence.getContacts({ approved: true }),
                        campusIntelligence.getEvents({ approved: true, upcoming: true }),
                        campusIntelligence.getPolicies({ approved: true }),
                    ]);
                    const c = contactsRes.status === 'fulfilled' ? (contactsRes.value?.data?.contacts || []) : [];
                    const e = eventsRes.status === 'fulfilled' ? (eventsRes.value?.data?.events || []) : [];
                    const p = policiesRes.status === 'fulfilled' ? (policiesRes.value?.data?.policies || []) : [];
                    setContacts(c); setEvents(e); setPolicies(p);
                    if (c.length || e.length || p.length) setHasData(true);
                }

                // Always try official docs — they use a simpler endpoint
                const docsRes = await officialDocuments.getActive();
                const d = docsRes?.data?.documents || [];
                setDocs(d);
                if (d.length) setHasData(true);
            } catch (err) {
                console.warn('[CampusHub] Data load error:', err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    // Filter by search
    const filteredContacts = contacts.filter(c =>
        !search || c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.title?.toLowerCase().includes(search.toLowerCase())
    );
    const filteredEvents = events.filter(e =>
        !search || (e.title || e.name)?.toLowerCase().includes(search.toLowerCase())
    );
    const filteredPolicies = policies.filter(p =>
        !search || p.title?.toLowerCase().includes(search.toLowerCase()) ||
        p.summary?.toLowerCase().includes(search.toLowerCase())
    );

    // Upcoming events: sort by date, take top 5
    const upcomingEvents = [...filteredEvents]
        .filter(e => e.date)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 5);

    const isPrimary = prominence === 'primary';

    return (
        <MotionDiv
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            style={{
                background: 'var(--surface-card)',
                borderRadius: 16,
                border: '1px solid var(--border-default)',
                boxShadow: isPrimary
                    ? '0 4px 20px rgba(0,0,0,0.07)'
                    : '0 2px 8px rgba(0,0,0,0.04)',
                overflow: 'hidden',
            }}
        >
            {/* ── Header ── */}
            <button
                onClick={() => setCollapsed(c => !c)}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: isPrimary ? '16px 20px' : '14px 16px',
                    background: isPrimary
                        ? 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)'
                        : 'var(--surface-raised)',
                    border: 'none', cursor: 'pointer',
                    borderBottom: collapsed ? 'none' : `1px solid ${isPrimary ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: isPrimary ? 34 : 28, height: isPrimary ? 34 : 28,
                        borderRadius: 8,
                        background: 'linear-gradient(135deg, #FFC107, #FF9800)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: isPrimary ? '0 2px 8px rgba(255,193,7,0.35)' : 'none',
                    }}>
                        <Building2 size={isPrimary ? 17 : 14} color="#000" />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                        <div style={{
                            fontSize: isPrimary ? 15 : 13,
                            fontWeight: 700,
                            color: isPrimary ? '#f1f5f9' : 'var(--text-primary)',
                            letterSpacing: '-0.01em',
                        }}>
                            Campus Intelligence
                        </div>
                        <div style={{ fontSize: 11, color: isPrimary ? '#94a3b8' : 'var(--text-muted)', marginTop: 1 }}>
                            {loading
                                ? 'Loading campus context…'
                                : hasData
                                    ? `Contacts · Events · Procedures`
                                    : 'Shared campus context'}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {hasData && (
                        <span style={{
                            fontSize: 10, fontWeight: 600,
                            background: 'rgba(34,197,94,0.15)',
                            color: '#4ADE80',
                            padding: '2px 8px', borderRadius: 10,
                            letterSpacing: '0.02em',
                            display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                            <CheckCircle size={9} />
                            LIVE
                        </span>
                    )}
                    {collapsed
                        ? <ChevronRight size={16} color={isPrimary ? '#94a3b8' : 'var(--text-muted)'} />
                        : <ChevronDown size={16} color={isPrimary ? '#94a3b8' : 'var(--text-muted)'} />
                    }
                </div>
            </button>

            {/* ── Body ── */}
            <AnimatePresence>
                {!collapsed && (
                    <MotionDiv
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div style={{ padding: '16px 20px' }}>
                            {/* Loading */}
                            {loading && (
                                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                                    <div style={{
                                        width: 20, height: 20, border: '2px solid var(--border-default)',
                                        borderTopColor: '#FFC107', borderRadius: '50%',
                                        animation: 'spin 0.8s linear infinite',
                                        margin: '0 auto 8px',
                                    }} />
                                    Loading campus context…
                                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                                </div>
                            )}

                            {!loading && !hasData && <EmptyState userRole={userRole} />}

                            {!loading && hasData && (
                                <>
                                    {/* Search */}
                                    {(contacts.length > 3 || events.length > 3 || policies.length > 3) && (
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            background: 'var(--surface-raised)', borderRadius: 8,
                                            padding: '6px 10px', marginBottom: 16,
                                            border: '1px solid var(--border-default)',
                                        }}>
                                            <Search size={13} color="#94a3b8" />
                                            <input
                                                type="text"
                                                value={search}
                                                onChange={e => setSearch(e.target.value)}
                                                placeholder="Search campus info…"
                                                style={{
                                                    flex: 1, border: 'none', background: 'none',
                                                    fontSize: 13, color: 'var(--text-primary)', outline: 'none',
                                                }}
                                            />
                                            {search && (
                                                <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                                                    <X size={13} color="#94a3b8" />
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {/* Official Docs Quick Links */}
                                    {docs.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <div style={{
                                                fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
                                                textTransform: 'uppercase', letterSpacing: '0.08em',
                                                marginBottom: 8,
                                            }}>
                                                Official Documents
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6 }}>
                                                {docs.slice(0, 6).map(doc => (
                                                    <DocQuickLink key={doc.id} doc={doc} />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Upcoming Events */}
                                    {upcomingEvents.length > 0 && (
                                        <CollapsibleSection
                                            title="Upcoming Events"
                                            icon={Calendar}
                                            count={upcomingEvents.length}
                                            defaultOpen={isPrimary}
                                        >
                                            {upcomingEvents.map(event => (
                                                <EventRow key={event.id} event={event} />
                                            ))}
                                        </CollapsibleSection>
                                    )}

                                    {/* Campus Contacts */}
                                    {filteredContacts.length > 0 && (
                                        <CollapsibleSection
                                            title="Campus Contacts"
                                            icon={Users}
                                            count={filteredContacts.length}
                                            defaultOpen={isPrimary || userRole === 'counselor' || userRole === 'support_staff'}
                                        >
                                            {filteredContacts.slice(0, 8).map(contact => (
                                                <ContactRow key={contact.id} contact={contact} />
                                            ))}
                                            {filteredContacts.length > 8 && (
                                                <p style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', paddingTop: 6 }}>
                                                    + {filteredContacts.length - 8} more contacts
                                                </p>
                                            )}
                                        </CollapsibleSection>
                                    )}

                                    {/* Policies / Procedures */}
                                    {filteredPolicies.length > 0 && (
                                        <CollapsibleSection
                                            title="Campus Procedures"
                                            icon={Shield}
                                            count={filteredPolicies.length}
                                            defaultOpen={userRole === 'support_staff'}
                                        >
                                            {filteredPolicies.slice(0, 5).map(policy => (
                                                <PolicyRow key={policy.id} policy={policy} />
                                            ))}
                                        </CollapsibleSection>
                                    )}

                                    {/* Context attribution */}
                                    <div style={{
                                        marginTop: 8, paddingTop: 12,
                                        borderTop: '1px solid var(--border-default)',
                                        display: 'flex', alignItems: 'center', gap: 6,
                                    }}>
                                        <Shield size={11} color="var(--text-muted)" />
                                        <span style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                                            Showing admin-approved campus and district context.
                                            Content is verified by your campus admin.
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </MotionDiv>
                )}
            </AnimatePresence>
        </MotionDiv>
    );
};

export default CampusIntelligenceHub;
